import {Field} from '@/hosted_fields/common/enums';
import GatewayScriptLoader from '@/hosted_fields/master/gateway-script-loader';
import Transformer from '@/helpers/transformer';
import CommManagerInterface from './interface';
import {Master} from '@/hosted_fields/common/enums';
import {ActionInnerMessage, GwTokenizationResponse} from '@/hosted_fields/common/types';
import Errors, {CbError} from '@/hosted_fields/common/errors';
import {Address} from '@/plugins/three_domain_secure/types';
import {Gateway} from '@/extensions/three_domain_secure/common/types';
import Logger from '@/utils/logger_old';
import DeviceDataHelpers from '@/helpers/device-data';
import {Braintree} from '@/extensions/three_domain_secure/handlers/braintree/types';

class TokenizationHandler {
  private gwConfig: any;
  private tokenConfig: any;
  private commonConfig: any;
  private startTime: any;
  private commMgr: CommManagerInterface;

  private gwScriptLoader: GatewayScriptLoader;

  constructor(commMgr: CommManagerInterface) {
    this.gwScriptLoader = new GatewayScriptLoader();
    this.commMgr = commMgr;
  }

  private init(gwConfig) {
    this.gwConfig = gwConfig;
    let gwName = Object.keys(gwConfig.tokenization_config).filter((k) => k != 'common_config')[0];
    this.tokenConfig = gwConfig.tokenization_config[gwName];
    this.commonConfig = gwConfig.tokenization_config.common_config;
  }

  public tokenize(gwConfig, payload): Promise<GwTokenizationResponse> {
    this.init(gwConfig);
    return this.loadScript(gwConfig).then(() => {
      return this.invoke(payload);
    });
  }

  public loadScript(gwConfig) {
    return this.gwScriptLoader.loadScript(gwConfig);
  }

  private invoke(payload): Promise<GwTokenizationResponse> {
    this.startTime = new Date().getTime();
    switch (this.tokenConfig.handler) {
      case Gateway.STRIPE:
        return this.stripeTokenization(payload);
      case Gateway.BRAINTREE:
        return this.braintreeTokenization(payload);
      case Gateway.AUTHORIZE_NET:
        return this.authorizeNetTokenization(payload);
      case Gateway.ECENTRIC:
      case Gateway.METRICS_GLOBAL:
      case Gateway.WINDCAVE:
      case Gateway.NMI:
      case Gateway.DLOCAL:
      case Gateway.Nuvei:
        return this.payFurlTokenization(payload);
      case Gateway.EBANX:
        return this.ebanxTokenization(payload);
    }
  }

  private logTokenization(http_code, error?) {
    try {
      var d = {};
      d['gw_time'] = new Date().getTime() - this.startTime;
      d['gw_type'] = this.tokenConfig.gw_name;
      d['gw_http_code'] = http_code;
      if (error) {
        d['gw_error_message'] = error.message;
        const err = new CbError({
          name: 'TokenizationError',
          message: d['gw_error_message'],
        });
        Logger.error(err, d);
      } else {
        const payload: ActionInnerMessage = {
          action: Master.Actions.CaptureKVL,
          data: d,
        };
        this.commMgr.invokeAction(payload);
      }
    } catch (error) {
      Logger.error(error, {
        description: 'Error occurred while logging tokenization event',
      });
    }
  }

  /* Stripe Tokenization */

  private stripeTokenization(payload): Promise<any> {
    if (payload.bankData) {
      return this.stripeBankAccountTokenization(payload);
    }
    return this.stripeCardTokenization(payload);
  }

  private stripeCardTransformer({cardData, additionalData}) {
    let exp = cardData[Field.Expiry].split('/');
    let out = {
      number: cardData[Field.Number].replace(/\s+/g, ''),
      cvc: cardData[Field.CVV],
      exp_month: exp[0],
      exp_year: exp[1],
    };
    if (cardData[Field.PREFERRED_NETWORK]) {
      out['networks'] = {
        preferred: cardData[Field.PREFERRED_NETWORK],
      };
    }
    if (additionalData) {
      const addr: Address = Transformer.billingAddress(additionalData);
      out['name'] = Transformer.fullName(addr.firstName, addr.lastName);
      out['address_line1'] = addr.addressLine1;
      out['address_line2'] = addr.addressLine2;
      out['address_city'] = addr.city;
      out['address_state'] = addr.stateCode || addr.state;
      out['address_zip'] = addr.zip;
      out['address_country'] = addr.countryCode;
    }
    return Transformer.clean(out);
  }

  private stripeCardTokenization(payload): Promise<any> {
    let cardData = this.stripeCardTransformer(payload);

    return new Promise((resolve, reject) => {
      if (this.tokenConfig.client_id) {
        window['Stripe'].setPublishableKey(this.tokenConfig.client_id);
      }
      window['Stripe'].card.createToken(cardData, (status, response) => {
        let err;
        if (response.error) {
          err = new CbError(response.error);
          reject(err);
        } else {
          resolve({token: response.id});
        }
        this.logTokenization(status, err);
      });
    });
  }

  private stripeBankAccountTransformer(bankData) {
    return {
      country: 'US',
      currency: this.gwConfig.gateway_currency,
      routing_number: bankData[Field.ROUTING_NUMBER],
      account_number: bankData[Field.ACCOUNT_NUMBER],
      account_holder_name: bankData[Field.NAME_ON_ACCOUNT],
      account_holder_type: bankData[Field.ACCOUNT_TYPE],
    };
  }

  private stripeBankAccountTokenization(payload): Promise<any> {
    let bankData = this.stripeBankAccountTransformer(payload.bankData);
    return new Promise((resolve, reject) => {
      window['Stripe'].bankAccount.createToken(bankData, (status, response) => {
        let err;
        if (response.error) {
          err = new CbError(response.error);
          reject(err);
        } else {
          resolve({token: response.id});
        }
        this.logTokenization(status, err);
      });
    });
  }

  /* Authorize.Net Tokenization */

  private authorizeNetTransformer({cardData, bankData, additionalData}) {
    let out = {};
    if (cardData) {
      let exp = cardData[Field.Expiry].split('/');
      out['cardData'] = {
        cardNumber: cardData[Field.Number].replace(/\s+/g, ''),
        month: exp[0],
        year: exp[1],
        cardCode: cardData[Field.CVV],
      };
      if (additionalData) {
        const addr = Transformer.billingAddress(additionalData);
        out['cardData']['zip'] = addr.zip;
        out['cardData']['fullName'] = Transformer.fullName(
          additionalData[Field.FIRST_NAME],
          additionalData[Field.LAST_NAME]
        );
      }
    } else if (bankData) {
      out['bankData'] = {
        accountNumber: bankData[Field.ACCOUNT_NUMBER],
        routingNumber: bankData[Field.ROUTING_NUMBER],
        nameOnAccount: bankData[Field.NAME_ON_ACCOUNT],
        accountType: bankData[Field.ACCOUNT_TYPE],
      };
    }
    return Transformer.clean(out);
  }

  private authorizeNetTokenization(payload): Promise<any> {
    let secureData = this.authorizeNetTransformer(payload);
    secureData['authData'] = {
      clientKey: this.tokenConfig.client_id,
      apiLoginID: this.tokenConfig.api_login_id,
    };
    return new Promise((resolve, reject) => {
      window['Accept'].dispatchData(secureData, (response) => {
        let status = 200,
          err;
        if (response.messages.resultCode === 'Error') {
          err = new CbError(response.messages.message[0].text);
          status = 400;
          return reject(err);
        }
        resolve({token: response.opaqueData.dataValue});
        this.logTokenization(status, err);
      });
    });
  }

  /* Braintree Tokenization */

  private braintreeTransformer({cardData, additionalData}) {
    let out = {
      creditCard: {
        number: cardData[Field.Number].replace(/\s+/g, ''),
        cvv: cardData[Field.CVV],
        expirationDate: cardData[Field.Expiry],
        options: {
          validate: false,
        },
      },
    };
    if (additionalData) {
      const addr: Address = Transformer.billingAddress(additionalData);
      let billing = {
        first_name: addr.firstName,
        last_name: addr.lastName,
        street_address: addr.addressLine1,
        extended_address: addr.addressLine2,
        locality: addr.city,
        postal_code: addr.zip,
        region: addr.stateCode || addr.state,
        country_code_alpha2: addr.countryCode,
      };
      out['creditCard']['billingAddress'] = billing;
      const cardholderName = `${addr.firstName || ''} ${addr.lastName || ''}`.trim();
      if (cardholderName) {
        out['creditCard']['cardholderName'] = cardholderName;
      }
    }
    if (this.tokenConfig.merchant_account_id) {
      out['authenticationInsight'] = true;
      out['merchantAccountId'] = this.tokenConfig.merchant_account_id;
    }
    return Transformer.clean(out);
  }

  private braintreeTokenization(payload): Promise<GwTokenizationResponse> {
    let cardData = this.braintreeTransformer(payload);
    return new Promise((resolve, reject) => {
      window.braintree.client
        .create({authorization: this.tokenConfig.client_id})
        .then((clientInstance) =>
          Promise.all([
            clientInstance.request({
              endpoint: 'payment_methods/credit_cards',
              method: 'post',
              data: cardData,
            }),
            DeviceDataHelpers.getBraintreeDeviceData(window['braintree'], clientInstance),
          ])
        )
        .then(([response, deviceData]: [any, Braintree.DeviceData]) => {
          let fDeviceData: {
            braintree: {
              fraud: Braintree.DeviceData;
              bin?: string;
              regulationEnvironment?: Braintree.REGULATION_ENVIRONMENT;
            };
          } = null;
          if (deviceData) {
            fDeviceData = {braintree: {fraud: deviceData}};
          }

          const creditCard: Braintree.TokenizedCard = response.creditCards[0];
          const tokenizationResponse: GwTokenizationResponse = {
            token: creditCard.nonce,
            deviceData: fDeviceData,
            additional_information: {
              braintree: {
                bin: creditCard.details && creditCard.details.bin,
                regulationEnvironment:
                  creditCard.authenticationInsight && creditCard.authenticationInsight.regulationEnvironment,
              },
            },
          };
          resolve(tokenizationResponse);
          this.logTokenization(response._httpStatus);
        })
        .catch((error) => {
          reject(error);
          this.logTokenization(error.details ? error.details.httpStatus : error.code, error);
        });
    });
  }

  /* Payfurl Tokenization */

  private payFurlTransformer({cardData, additionalData}) {
    let out = {};
    if (cardData) {
      out['cardData'] = {
        number: cardData[Field.Number].replace(/\s+/g, ''),
        expiry: cardData[Field.Expiry],
        cvv: cardData[Field.CVV],
      };
      if (additionalData) {
        out['cardData']['name'] = Transformer.fullName(
          additionalData[Field.FIRST_NAME],
          additionalData[Field.LAST_NAME]
        );
        out['cardData']['additionalData'] = {
          DocumentId: additionalData['document'] ? additionalData['document']['number'] : '',
          Name: Transformer.fullName(additionalData[Field.FIRST_NAME], additionalData[Field.LAST_NAME]),
          Email: additionalData[Field.EMAIL],
        };
      }
    }
    return Transformer.clean(out);
  }

  private payFurlTokenization(payload): Promise<any> {
    const secureData = this.payFurlTransformer(payload);
    const cardData = secureData['cardData'];
    return new Promise((resolve, reject) => {
      const pf = window['payfurl'].init(
        this.tokenConfig.sandbox_site ? 'sandbox' : 'production',
        this.tokenConfig.client_id,
        true
      );
      pf.onFailure((message) => {
        const err = new CbError(message);
        const status = 400;
        this.logTokenization(status, err);
        return reject(err);
      });
      pf.onSuccess((message) => {
        resolve({token: message.token});
        this.logTokenization(200);
      });
      pf.tokeniseCard(
        this.tokenConfig.provider_id,
        cardData.number,
        cardData.expiry,
        cardData.name,
        cardData.cvv,
        cardData.additionalData
      );
    });
  }

  private ebanxTransformer({cardData, additionalData}) {
    let out = {};
    if (cardData) {
      out['cardData'] = {
        number: cardData[Field.Number].replace(/\s+/g, ''),
        cvv: cardData[Field.CVV],
      };
      if (cardData[Field.Expiry]) {
        let exp = cardData[Field.Expiry].split('/');
        if (exp[1].length == 2) {
          out['cardData']['expiry'] = exp[0].concat('/20').concat(exp[1]);
        } else {
          out['cardData']['expiry'] = exp[0].concat('/').concat(exp[1]);
        }
      }
      if (additionalData) {
        out['cardData']['name'] = Transformer.fullName(
          additionalData[Field.FIRST_NAME],
          additionalData[Field.LAST_NAME]
        );
        const addr: Address = Transformer.billingAddress(additionalData);
        out['name'] = Transformer.fullName(addr.firstName, addr.lastName);
        out['address_line1'] = addr.addressLine1;
        out['address_line2'] = addr.addressLine2;
        out['address_city'] = addr.city;
        out['address_state'] = addr.stateCode || addr.state;
        out['address_zip'] = addr.zip;
        out['address_country'] = addr.countryCode;
        if (additionalData['document']) {
          out['cardData']['document'] = {
            number: additionalData['document']['number'],
            type: additionalData['document']['type'],
          };
        }
      }
    }
    return Transformer.clean(out);
  }

  private ebanxTokenization(payload): Promise<any> {
    const secureData = this.ebanxTransformer(payload);
    const cardData = secureData['cardData'];
    const country = secureData['address_country'] ? secureData['address_country'].toLowerCase() : '';
    const tokenizeOptions = {
      card: {
        number: cardData.number,
        dueDate: cardData.expiry,
        holderName: cardData.name,
      },
      paymentTypeCode: 'creditcard', // we support only creditcard
      countryCode: country,
    };

    const docInformation = {
      type: country.concat('_').concat(cardData.document.type),
      document: cardData.document.number,
    };
    return new Promise((resolve, reject) => {
      const ebanx = window['EBANX'];
      ebanx.init({
        publicIntegrationKey: this.tokenConfig.public_integration_key,
        country: country,
        mode: this.tokenConfig.sandbox_site ? 'test' : 'production',
      });

      this.validateEbanxDocumentId(ebanx, docInformation, country)
        .then((isValid) => {
          return ebanx.cardTokenizer.tokenize(tokenizeOptions).then((tokenizedCard) => {
            resolve({token: tokenizedCard.token});
            this.logTokenization(200);
          });
        })
        .catch((error) => {
          reject(error);
          this.logTokenization(error.details ? error.details.httpStatus : error.code, error);
        });
    });
  }

  validateEbanxDocumentId(ebanx: any, docInformation: any, country: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const ebanxDocCountryCodes = ['ar', 'br', 'uy'];
      if (!(country && ebanxDocCountryCodes.includes(country))) {
        resolve(true);
      }
      ebanx.utils.document.check(docInformation).then((verificationData) => {
        if (
          verificationData.status &&
          verificationData.status == 'success' &&
          verificationData.data &&
          verificationData.data.document &&
          verificationData.data.document.isValid
        ) {
          resolve(true);
        } else {
          reject(new CbError(Errors.invalidEbanxDocument));
        }
      });
    });
  }
}

export default TokenizationHandler;
